// Linear hand-off codes (poc CT-009, architecture 7.16, module-14). Linear owns a handed-off ticket's status: the
// tool reads the issue's workflow state TYPE, which every Linear team has, and shows the state's own NAME.

/** Linear's workflow state types. Real Linear gives a Duplicate state its own `duplicate` type. */
export const LINEAR_STATE_TYPES = [
  'triage',
  'backlog',
  'unstarted',
  'started',
  'completed',
  'canceled',
  'duplicate',
] as const;
export type LinearStateType = (typeof LINEAR_STATE_TYPES)[number];

/**
 * Where a Linear state type puts the ticket (module-14 Section 4). Ticket Core picks the status in the ticket's
 * own workflow (`apply_linear_state`):
 *   ready        Ready / the legacy Assigned status (Accepted when the ticket has no assignee)
 *   in_progress  In progress (the stage default)
 *   completed    bug: Fixed (awaiting the reporters' confirmation); feature request: Shipped
 *   canceled     Not actioned (the stage default), with the Linear state as the reason
 */
export const LINEAR_TARGETS = ['ready', 'in_progress', 'completed', 'canceled'] as const;
export type LinearTarget = (typeof LINEAR_TARGETS)[number];

const TARGET_BY_STATE_TYPE: Record<LinearStateType, LinearTarget> = {
  // Not started yet: a team with triage on, or someone moving the issue back, still means "waiting to start".
  triage: 'ready',
  backlog: 'ready',
  unstarted: 'ready',
  started: 'in_progress',
  completed: 'completed',
  canceled: 'canceled',
  duplicate: 'canceled',
};

/** The target for a Linear state type; null for a type the tool doesn't know (the sync skips and flags it). */
export function linearTargetOf(stateType: string): LinearTarget | null {
  return (TARGET_BY_STATE_TYPE as Record<string, LinearTarget | undefined>)[stateType] ?? null;
}

/**
 * A ticket's hand-off as the pages show it: `pending` (queued or retrying), `done` (the Linear issue exists) or
 * `failed` (the deliverer gave up; "Retry hand-off" queues it again).
 */
export const LINEAR_HANDOFF_STATES = ['pending', 'done', 'failed'] as const;
export type LinearHandoffState = (typeof LINEAR_HANDOFF_STATES)[number];
